import { useEffect, useState } from 'react';
import type { WPreviewPhotoView } from '@zola/svc-web-api-ts-client';
import { fetchPreviewPhoto } from '../../api/previewPhotoApi';

/**
 * Hook for fetching preview photos and returning the first
 * pre:
 * delayFetch: if true, doesn't fetch; if false, fetches. use to conditionally fetch
 * selectionOverride: ordered array of values to return over existing order of preview photos; if none of prioritized available, default to existing order
 *
 * post:
 * chosen preview photo url
 */
const usePreviewPhoto = (
  delayFetch?: boolean,
  selectionOverride?: WPreviewPhotoView.SourceEnum[]
): string | undefined => {
  const [previewPhoto, setPreviewPhoto] = useState<string | undefined>();

  useEffect(() => {
    async function fetchPhoto() {
      const previewPhotoRes = await fetchPreviewPhoto();

      const selectPreviewImage = (): WPreviewPhotoView | undefined => {
        if (!(previewPhotoRes?.preview_photos && previewPhotoRes?.preview_photos.length > 0)) {
          return undefined;
        }
        if (selectionOverride) {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < selectionOverride.length; i++) {
            const currSource = selectionOverride[i];
            const previewPhotoFromSource = previewPhotoRes?.preview_photos?.filter(
              p => p.source === currSource
            )[0];
            if (previewPhotoFromSource) return previewPhotoFromSource;
          }
        }
        return previewPhotoRes.preview_photos[0];
      };

      let previewImageUrl = selectPreviewImage()?.url;
      if (previewImageUrl) {
        previewImageUrl =
          previewImageUrl.indexOf('http') < 0 ? `https:${previewImageUrl}` : previewImageUrl;
        setPreviewPhoto(previewImageUrl);
      }
    }

    // Fetch if we don't need to delay fetch and if preview photo hasn't already been set
    if (!delayFetch && !previewPhoto) {
      fetchPhoto();
    }
  }, [delayFetch, previewPhoto, selectionOverride]);

  return previewPhoto;
};

export default usePreviewPhoto;
